import * as React from "react";
import { useEffect } from "react";
import { connect } from "react-redux";
import queryString, { stringify } from "query-string";
import { ApplicationState } from "store";
import {
    actionCreators as uiActionCreators,
    getActiveNavigationComponent,
    getFastTrackStatus,
    getPreviewedExtraPage,
} from "ui/store";
import {
    actionCreators as burdeApiContentActionCreators,
    getIsLoadingLayoutOptions,
} from "burde-api-content/store";
import { actionCreators as eventActionCreators } from "event/store";
import { actionCreators, getCalendar } from "page/calendar/store";
import { RouteComponentProps, withRouter } from "react-router";
import { CalendarState, TextInformation } from "burde";
import { CalendarSizes, CalendarType } from "svg/constants";
import { getCalendarByTemporaryCalendarCode } from "calendar/service";
import { getPublishedCalendarThemes } from "content/theme/service";
import { getComponentRoute } from "./helper";
import { getCalendarStateFromPrintInfo } from "ui/helper";
import {
    DefaultCoverTextCalendarA5,
    DefaultCoverTextCalendarA6,
    DefaultCoverTextCalendarB5,
    DefaultCoverTextNotebookA5,
    DefaultCoverTextNotebookA6,
    DefaultCoverTextNotebookB5,
    NavigationComponent,
    NavigationComponents,
} from "ui/constants";

type CalendarQueryHandlerProps = {
    calendar: CalendarState;
    routeType: string;
    activeComponent: NavigationComponent;
    setActiveCalendar: (calendar?: CalendarState) => any;
    updateCalendarType: (calendarType: CalendarType) => any;
    updateCoverTextCollection: (collection: TextInformation[]) => any;
} & typeof actionCreators &
    typeof burdeApiContentActionCreators &
    typeof uiActionCreators &
    typeof eventActionCreators &
    RouteComponentProps<{
        step?: string;
        sizeName?: string;
        location?: any;
        history?: any;
    }>;

const CalendarUrlHandler: React.FC<CalendarQueryHandlerProps> = ({
    calendar,
    routeType,
    activeComponent,
    match: {
        params: { step: routeStep, sizeName: routeSize },
    },
    location,
    history,
    setActiveCalendar,
    updateShowLoginModal,
    updateInitialFrontCoverSectionId,
    updateInitialBackCoverSectionId,
    SetFastTrack,
    UpdateActiveNavigationComponent,
    updateCoverTextCollection,
    updateCalendarType,
}) => {
    const parsedQuery = queryString.parseUrl(location.search);

    useEffect(() => {
        const routeSizeMismatch = () =>
            !routeSize ||
            (routeSize &&
                routeSize.toLowerCase() != calendar.calendarSize.toLowerCase());

        const routeStepMismatch = () =>
            parsedQuery.query.fasttrack
                ? true
                : routeStep !== activeComponent.step;

        const routeTypeMismatch = () =>
            !routeType ||
            (routeType &&
                routeType.toLowerCase() != calendar.calendarType.toLowerCase());

        // If the size used in the route doesn't match the one in printInfo,
        // or if the step used in the route doesn't match the one in the state,
        // redirect to a valid route and update ActiveNavigationComponent
        if (routeSizeMismatch() || routeStepMismatch() || routeTypeMismatch()) {
            UpdateActiveNavigationComponent(NavigationComponents[0]);

            const route = getComponentRoute(
                NavigationComponents[0].route,
                calendar.calendarSize,
                calendar.calendarType,
            );

            history.push({
                pathname: route,
                search: "?" + stringify(parsedQuery.query),
            });
        }
    }, [parsedQuery.query, routeStep, routeSize, routeType]);

    // The following segment takes care of any query parameters.
    if (location && location.pathname && location.search && history) {
        let queryChanged = false;

        let newLocation = location.pathname;

        // Load a temporary calendar by code if present
        if (parsedQuery.query.code) {
            const codeFromUrl = parsedQuery.query.code;

            getCalendarByTemporaryCalendarCode(codeFromUrl).then(
                (printInfo) => {
                    if (printInfo !== null && printInfo !== undefined) {
                        const calendarState = getCalendarStateFromPrintInfo(
                            printInfo,
                        );
                        calendarState.ownerPage.id = 0;
                        setActiveCalendar({
                            ...calendarState,
                            // Make the calendar "new" to not override old theme
                            isTheme: false,
                            name: "",
                            startDate: calendar.startDate,
                        });
                    }
                },
            );

            delete parsedQuery.query.code;
            queryChanged = true;
        }

        // Enable or disable fasttrack
        if (parsedQuery.query.fasttrack === "true") {
            SetFastTrack(true);
            delete parsedQuery.query.fasttrack;
            queryChanged = true;
        } else if (parsedQuery.query.fasttrack === "false") {
            SetFastTrack(false);
            delete parsedQuery.query.fasttrack;
            queryChanged = true;
        }

        // If a theme has been provided as a parameter, apply it
        if (parsedQuery.query.theme) {
            const themeNameFromUrl = parsedQuery.query.theme.replace(/_/g, " ");

            const getThemes = async () => await getPublishedCalendarThemes();

            getThemes().then((themes) => {
                const theme = themes.find(
                    (x) =>
                        x.name.toLowerCase() == themeNameFromUrl.toLowerCase(),
                );

                if (theme !== null && theme !== undefined) {
                    const getCalendarState = async () =>
                        await getCalendarStateFromPrintInfo(theme);

                    getCalendarState().then((calendarState) => {
                        setActiveCalendar({
                            ...calendarState,
                            // Make the calendar "new" to not override old theme
                            isTheme: false,
                            name: "",
                            startDate: calendar.startDate,
                        });

                        // If theme and summary provided at the same time, the summary procedure has to be done
                        // after the theme is set.
                        delete parsedQuery.query.theme;
                        queryChanged = true;
                    });
                }
            });
        }

        if (
            parsedQuery.query.notebook === "true" &&
            calendar.calendarType !== CalendarType.Notebook
        ) {
            if (
                calendar.calendarSize.toLowerCase() ===
                CalendarSizes.A5.toLowerCase()
            ) {
                updateCoverTextCollection([DefaultCoverTextNotebookA5]);
            } else if (
                calendar.calendarSize.toLowerCase() ===
                CalendarSizes.B5.toLowerCase()
            ) {
                updateCoverTextCollection([DefaultCoverTextNotebookB5]);
            } else {
                updateCoverTextCollection([DefaultCoverTextNotebookA6]);
            }
            updateCalendarType(CalendarType.Notebook);
            delete parsedQuery.query.notebook;
            queryChanged = true;
        } else if (
            parsedQuery.query.notebook === "false" &&
            calendar.calendarType === CalendarType.Notebook
        ) {
            if (
                calendar.calendarSize.toLowerCase() ===
                CalendarSizes.A5.toLowerCase()
            ) {
                updateCoverTextCollection([DefaultCoverTextCalendarA5]);
            } else if (
                calendar.calendarSize.toLowerCase() ===
                CalendarSizes.B5.toLowerCase()
            ) {
                updateCoverTextCollection([DefaultCoverTextCalendarB5]);
            } else {
                updateCoverTextCollection([DefaultCoverTextCalendarA6]);
            }
            updateCalendarType(CalendarType.Calendar);
            delete parsedQuery.query.notebook;
            queryChanged = true;
        }

        if (parsedQuery.query.showLoginModal) {
            updateShowLoginModal(true);
            delete parsedQuery.query.showLoginModal;
            queryChanged = true;
        }

        if (parsedQuery.query.initialFrontCoverSectionId !== undefined) {
            let initialFrontCoverSectionId = parsedQuery.query
                .initialFrontCoverSectionId
                ? parseInt(parsedQuery.query.initialFrontCoverSectionId)
                : null;
            updateInitialFrontCoverSectionId(initialFrontCoverSectionId);
            delete parsedQuery.query.initialFrontCoverSectionId;
            queryChanged = true;
        }

        if (parsedQuery.query.initialBackCoverSectionId !== undefined) {
            let initialBackCoverSectionId = parsedQuery.query
                .initialBackCoverSectionId
                ? parseInt(parsedQuery.query.initialBackCoverSectionId)
                : null;
            updateInitialBackCoverSectionId(initialBackCoverSectionId);
            delete parsedQuery.query.initialBackCoverSectionId;
            queryChanged = true;
        }

        if (queryChanged) {
            history.push({
                pathname: newLocation,
                search: "?" + stringify(parsedQuery.query),
            });
        }
    }

    return <></>;
};

const mapStateToProps = (state: ApplicationState) => ({
    calendar: getCalendar(state),
    activeComponent: getActiveNavigationComponent(state),
    fastTrackStatus: getFastTrackStatus(state),
    previewedExtraPage: getPreviewedExtraPage(state),
    isLoadingLayoutOptions: getIsLoadingLayoutOptions(state),
});

const mapDispatchToProps = {
    ...actionCreators,
    ...uiActionCreators,
    ...burdeApiContentActionCreators,
    ...eventActionCreators,
};

const componentWithRouter = withRouter(CalendarUrlHandler);

export default connect(
    mapStateToProps,
    mapDispatchToProps,
)(componentWithRouter);
